import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import type { ApplyBizStatus, ApplyRow } from '@hq/api-sdk';
import { formatDisplayTime, formatQty } from '@hq/utils';
import { ApiError } from '@hq/api-sdk';
import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { Alert, Button, Card, Select, Space, Switch, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { hq } from '../api/client';
import { MoneyText } from '../components/Money';
import { SlaBadge } from '../components/SlaBadge';
import { COL, ellipsisCell } from '@hq/ui-kit';
import { ApplyStatusTag } from '../components/StatusTag';

const PAGE_SIZE = 20;

type StatusFilter = 'ALL' | ApplyBizStatus;

const STATUS_OPTIONS: { label: string; value: StatusFilter }[] = [
  { label: '全部', value: 'ALL' },
  { label: '草稿', value: 'DRAFT' },
  { label: '已提交', value: 'SUBMITTED' },
  { label: '审批中', value: 'APPROVING' },
  { label: '已通过', value: 'APPROVED' },
  { label: '已判源', value: 'ALLOCATED' },
  { label: '配送中', value: 'DELIVERING' },
  { label: '部分签收', value: 'PART_SIGNED' },
  { label: '已签收', value: 'SIGNED' },
  { label: '已驳回', value: 'REJECTED' },
  { label: '已取消', value: 'CANCELED' },
  { label: '已关闭', value: 'CLOSED' }
];

/**
 * 申领列表：后端为游标分页（lastId + hasMore，无 total，docs/05 §6-11），只做「加载更多」。
 * 状态与「只看我的」筛选在服务端完成（ApplyListQuery），切换后重新从第一页取。
 */
export default function ApplyList() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<StatusFilter>('ALL');
  const [mineOnly, setMineOnly] = useState(true);

  const query = useInfiniteQuery({
    queryKey: ['apply', 'list', status, mineOnly],
    placeholderData: keepPreviousData,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      hq.apply.list({
        lastId: pageParam,
        size: PAGE_SIZE,
        bizStatus: status === 'ALL' ? undefined : status,
        mine: mineOnly
      }),
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextLastId : undefined)
  });

  const rows = query.data?.pages.flatMap((page) => page.items) ?? [];

  const columns: ColumnsType<ApplyRow> = [
    {
      title: '单号',
      dataIndex: 'bizNo',
      width: COL.bizNo,
      render: (value: string, row) => <Typography.Link onClick={() => navigate(`/apply/${row.id}`)}>{value}</Typography.Link>
    },
    { title: '标题', dataIndex: 'applyTitle', ellipsis: { showTitle: false }, render: ellipsisCell('applyTitle') },
    { title: '申请科室', dataIndex: 'applyDeptName', width: COL.dept, render: (value: string | null) => value || '-' },
    { title: '状态', dataIndex: 'bizStatus', width: COL.status, render: (value: ApplyRow['bizStatus']) => <ApplyStatusTag status={value} /> },
    { title: '数量', dataIndex: 'totalQty', width: COL.qty, align: 'right', render: (value: number) => formatQty(value) },
    { title: '金额', dataIndex: 'totalAmount', width: COL.amount, align: 'right', render: (value: number) => <MoneyText value={value} /> },
    {
      title: '时效',
      dataIndex: 'slaDeadline',
      width: 150,
      render: (value: string | null, row) => (
        <SlaBadge
          deadline={value}
          finished={['SIGNED', 'CLOSED', 'CANCELED', 'REJECTED'].includes(row.bizStatus)}
          finishedText={row.bizStatus === 'SIGNED' ? '已签收' : undefined}
        />
      )
    },
    {
      title: '申请时间',
      dataIndex: 'bizDate',
      width: COL.time,
      render: (value: string) => formatDisplayTime(value)
    }
  ];

  return (
    <Card
      className="page-card"
      title="我的申领"
      extra={
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => query.refetch()} loading={query.isFetching}>
            刷新
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/apply/create')}>
            新建申领
          </Button>
        </Space>
      }
    >
      <Space wrap style={{ marginBottom: 12 }}>
        <Select<StatusFilter>
          value={status}
          options={STATUS_OPTIONS}
          style={{ width: 140 }}
          onChange={(value) => setStatus(value)}
        />
        <Space size={6}>
          <Switch size="small" checked={mineOnly} onChange={setMineOnly} />
          <Typography.Text type="secondary">只看我的</Typography.Text>
        </Space>
      </Space>

      {query.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="列表加载失败"
          description={query.error instanceof ApiError ? query.error.friendlyMessage : String(query.error)}
        />
      ) : null}
      <Table<ApplyRow>
        rowKey="id"
        size="middle"
        columns={columns}
        dataSource={rows}
        loading={query.isPending}
        pagination={false}
        scroll={{ x: 'max-content' }}
        locale={{ emptyText: query.isError ? '加载失败，请重试' : '暂无申领单' }}
        onRow={(row) => ({ onDoubleClick: () => navigate(`/apply/${row.id}`) })}
      />
      <Space style={{ marginTop: 16 }}>
        <Typography.Text type="secondary">已加载 {rows.length} 条</Typography.Text>
        {query.hasNextPage ? (
          <Button onClick={() => query.fetchNextPage()} loading={query.isFetchingNextPage}>
            加载更多
          </Button>
        ) : rows.length > 0 ? (
          <Typography.Text type="secondary">没有更多了</Typography.Text>
        ) : null}
      </Space>
    </Card>
  );
}
